import React from "react";
import ReactDOM from "react-dom/client";
import _ from "lodash";
import RpaScoringBadminton from "./RpaScoringBadminton";
import RpaScoringFutsal from "./RpaScoringFutsal";

/** @typedef {import('../transformers/RpaPlanningTransformer').FormattedRpaScheduleGridFrScoringObj } FormattedRpaScheduleGridFrScoringObj */
/** @typedef {import('../models/RpaResourceModel').RpaResourceItem } RpaResourceItem */
/** @typedef {import('../models/RpaActivityModel').ActivityType } ActivityType */
/** @typedef {import('../controllers/RpaResourceController').SearchScoringObj } SearchScoringObj */
/** @typedef {import('../controllers/RpaResourceController').RpaKeyAndPadCenterObj } RpaKeyAndPadCenterObj */

/**
 * @typedef {object} CountDownObj
 * @property {number} hours - Heures restantes
 * @property {number} minutes - Minutes restantes
 * @property {number} seconds - Secondes restantes
 * @property {string} str - Chaîne formatée du compte à rebours
 */

/**
 * @typedef {object} RpaScoringProps
 * @property {number} rpaResourceId - Identifiant de la ressource RPA
 * @property {FormattedRpaScheduleGridFrScoringObj} nearlyRpaScheduleGrid - Grille horaire formatée la plus proche
 * @property {RpaResourceItem} rpaResource - Resource RPA
 * @property {?string} scoringBg - Image de fond du scoring
 * @property {?string} todayDateStr - Date du jour formatée
 * @property {Date} nextRefreshDate - Date de la prochaine actualisation du scoring
 * @property {string} nextRefreshDateStr - Date de la prochaine actualisation du scoring formatée
 * @property {boolean} almostEnd - Indicateur si la réservation est presque terminée
 * @property {string} liveContentHtml - Contenu HTML dynamique de la ressource RPA
 * @property {string} logoHtml - Contenu HTML du logo de la ressource RPA
 * @property {string} scoringWaitContentHtml - Contenu HTML dynamique affiché en attendant le chargement du scoring
 * @property {boolean} showReservation - Indicateur si le nom de la réservation doit être affiché
 * @property {boolean} inProgress - Indicateur si le compteur est en cours
 * @property {CountDownObj} countDownObj - Objet du compte à rebours
 * @property {string} startTimeRefreshScoring - Date de début d'actualisation automatique du scoring au format timestamp
 * @property {string} endTimeRefreshScoring - Date de fin d'actualisation automatique du scoring au format timestamp
 * @property {number} startRefreshDateTime - Date de début d'actualisation automatique du scoring
 * @property {number} endRefreshDateTime - Date de fin d'actualisation automatique du scoring
 * @property {?ActivityType} activityType - Type d'activité de la resource
 * @property {boolean} hasScoring - Si la ressource possède un scoreur
 * @property {boolean} hasTimer - Si la ressource possède un timer
 * @property {RpaKeyAndPadCenterObj} keyAndPadCenterObj - Objet des keycodes et boutons du pad central
 */

class RpaScoring extends React.Component {
  /**
   * @param {RpaScoringProps} props
   */
  constructor(props) {
    super(props);

    this.state = /** @type {RpaScoringProps} */ ({
      rpaResourceId: props.rpaResourceId,
      nearlyRpaScheduleGrid: {},
      rpaResource: {},
      scoringBg: null,
      todayDateStr: null,
      nextRefreshDate: new Date(),
      nextRefreshDateStr: "",
      almostEnd: false,
      inProgress: false,
      liveContentHtml: "",
      logoHtml: "",
      scoringWaitContentHtml: "",
      showReservation: false,
      countDownObj: {
        hours: 0,
        minutes: 0,
        seconds: 0,
        str: ""
      },
      startRefreshDateTime: parseInt(props.startTimeRefreshScoring, 10),
      endRefreshDateTime: parseInt(props.endTimeRefreshScoring, 10),
      activityType: null,
      hasScoring: false,
      hasTimer: false,
      keyAndPadCenterObj: {}
    });
  }

  async componentDidMount() {
    // NOTE on récupère les dates de rafraichissement avant de récupérer les données de scoring
    await this.fetchRefreshDatesTime();

    // NOTE on récupère les données de scoring et on lance le compte à rebours
    await this.fethScoringDataAndLaunchCountdown();

    // NOTE toutes mes 5 secondes on rafraichit les données de scoring si on est dans l'intervalle et s'il n'y a pas de compte à rebours en cours
    setInterval(() => {
      this.refreshScoringData();
    }, 5000);

    // NOTE toutes les minutes on rafraichit les dates de rafraichissement, le fetch ne sera fait que si on est entre 00:00 et 00:15
    setInterval(() => {
      this.refreshDatesTime();
    }, 60000);
  }

  /**
   * Permet de savoir si on est dans l'intervalle de rafraichissement automatique du scoring par rapport à la date actuelle
   * @returns {boolean}
   */
  isInInterval() {
    var today = new Date();

    return today.getTime() >= this.state.startRefreshDateTime && today.getTime() <= this.state.endRefreshDateTime;
  }

  /**
   * Récupère les données de scoring et lance le compte à rebours
   * @returns {Promise<void>}
   */
  async fethScoringDataAndLaunchCountdown() {
    var response = await fetch(`/api/rpa-resource/search/scoring?rpaResourceId=${this.state.rpaResourceId}`, {
        headers: {
          Authorization: "gestiaweb"
        }
      }),
      scoringData = /** @type {SearchScoringObj} */ (await response.json());

    return this.setState(
      {
        nearlyRpaScheduleGrid: scoringData.nearlyRpaScheduleGrid,
        rpaResource: scoringData.rpaResource,
        scoringBg: scoringData.scoringBg,
        todayDateStr: scoringData.todayDateStr,
        nextRefreshDate: scoringData.nextRefreshDate,
        nextRefreshDateStr: scoringData.nextRefreshDateStr,
        liveContentHtml: scoringData.liveContentHtml,
        scoringWaitContentHtml: scoringData.scoringWaitContentHtml,
        logoHtml: scoringData.logoHtml,
        showReservation: scoringData.showReservation,
        activityType: scoringData.rpaResource.rpa_activity.activity_type,
        hasScoring: scoringData.rpaResource.has_scoring,
        hasTimer: scoringData.rpaResource.has_timer,
        keyAndPadCenterObj: scoringData.keyAndPadCenterObj
      },
      () => {
        this.launchCountdown();
      }
    );
  }

  /**
   * Récupère les dates de rafraichissement du scoring
   * @returns {Promise<void>}
   */
  async fetchRefreshDatesTime() {
    var response = await fetch("/api/rpa-resource/search/refreshdatescoring", {
        headers: {
          Authorization: "gestiaweb"
        }
      }),
      refreshDatesScoringData = await response.json();

    return this.setState({
      startRefreshDateTime: refreshDatesScoringData.startRefreshDateTime,
      endRefreshDateTime: refreshDatesScoringData.endRefreshDateTime
    });
  }

  /**
   * Rafraichit les données de scoring si on est dans l'intervalle et s'il n'y a pas de compte à rebours en cours
   * @returns {Promise<void>}
   */
  async refreshScoringData() {
    var inProgress = this.state.inProgress;

    if (!this.isInInterval() || !this.state.hasTimer) {
      return;
    }

    if (inProgress) {
      return;
    }

    this.stopCountdown();

    await this.fethScoringDataAndLaunchCountdown();
  }

  /**
   * Si on est entre 00:00 et 00:15 on rafraichit les dates de rafraichissement
   * @returns {Promise<void>}
   */
  async refreshDatesTime() {
    var now = new Date();

    if (!this.state.hasTimer) {
      return;
    }

    if (now.getHours() === 0 && now.getMinutes() >= 0 && now.getMinutes() <= 15) {
      await this.fetchRefreshDatesTime();
    } else {
      return;
    }
  }

  /**
   * Permet de lancer le compte à rebours
   * @returns {void}
   */
  launchCountdown() {
    if (!this.state.nearlyRpaScheduleGrid || !this.state.hasTimer) {
      this.setState({
        countDownObj: {
          hours: 0,
          minutes: 0,
          seconds: 0,
          str: "00 : 00 : 00"
        }
      });
      return;
    }

    this.setState({ almostEnd: false });

    this.updateCountdown();

    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  /**
   * Arrêt du compte à rebours
   */
  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * Met à jour le compteur de temps restant
   */
  updateCountdown() {
    var endTime = this.state.nearlyRpaScheduleGrid.endTime,
      now = new Date(),
      nowTime = now.getTime(),
      diff = endTime - nowTime,
      diffMinutes = Math.abs(nowTime - endTime) / (1000 * 60),
      hours,
      minutes,
      seconds;

    if (diffMinutes < 0) diff = 0;

    hours = Math.floor(diff / (1000 * 60 * 60));
    minutes = Math.floor((diff / (1000 * 60)) % 60);
    seconds = Math.floor((diff / 1000) % 60);

    if (diffMinutes <= 5) {
      this.setState({ almostEnd: true });
    }

    if (hours <= 0 && minutes <= 0 && seconds <= 0) {
      this.setState({
        inProgress: false,
        countDownObj: {
          hours: 0,
          minutes: 0,
          seconds: 0,
          str: "00 : 00 : 00"
        }
      });
    } else {
      this.setState({
        inProgress: true,
        countDownObj: {
          hours,
          minutes,
          seconds,
          str: `${hours.toString().padStart(2, "0")} : ${minutes.toString().padStart(2, "0")} : ${seconds.toString().padStart(2, "0")}`
        }
      });
    }
  }

  /**
   * Lors du clic sur le bouton plein écran on passe en mode plein écran
   */
  onClickOnFullScreeBtn() {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  }

  render() {
    var countdownClassName = "rparesourcelive__countdown-text",
      rpaResourceLiveStyle = {},
      rparesourceliveHeaderTitleStyle = {
        backgroundColor: this.state.rpaResource?.color || "transparent"
      },
      rpaResourceLiveGridRows = "auto auto 1fr";

    if (this.state.liveContentHtml) {
      rpaResourceLiveGridRows += " minmax(auto, 150px)";
    }

    if (this.state.almostEnd) {
      countdownClassName += " rparesourcelive__countdown-text--end";
    }

    rpaResourceLiveStyle.gridTemplateRows = rpaResourceLiveGridRows;

    if (this.state.scoringBg) {
      rpaResourceLiveStyle.backgroundImage = `url(${this.state.scoringBg})`;
    }

    return (
      <div className="rparesourcelive" style={rpaResourceLiveStyle}>
        <div className="rparesourcelive__header">
          <div className="rparesourcelive__header-left">
            <div className="rparesourcelive__header-logo" dangerouslySetInnerHTML={{ __html: this.state.logoHtml }} />
          </div>
          <div className="rparesourcelive__header-center">
            <div className="rparesourcelive__header-title" style={rparesourceliveHeaderTitleStyle}>
              {this.state.rpaResource.name}
            </div>
            <div className="rparesourcelive__header-resa-name">
              {this.state.nearlyRpaScheduleGrid && this.state.showReservation ? (
                <>
                  {this.state.nearlyRpaScheduleGrid.reservationStrName}
                  {this.state.nearlyRpaScheduleGrid.checked ? <span className="material-symbols-outlined">all_match</span> : <span className="material-symbols-outlined material-icons--danger">hourglass_empty</span>}
                </>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="rparesourcelive__header-right">
            <h3 className="rparesourcelive__header-date">{this.state.todayDateStr}</h3>
            <div className="rparesourcelive__header-buttons">
              <button className="rparesourcelive__header-button" onClick={this.onClickOnFullScreeBtn.bind(null)}>
                <span className="material-symbols-outlined">fullscreen</span>
              </button>
            </div>
          </div>
        </div>
        <div className="rparesourcelive__hours">
          <div className="rparesourcelive__hours-str">{this.state.nearlyRpaScheduleGrid ? <>{this.state.nearlyRpaScheduleGrid.hoursStrName}</> : ""}</div>
          <div className="rparesourcelive__refresh-infos">Prochaine mise à jour : {this.state.nextRefreshDateStr}</div>
        </div>

        {this.state.hasTimer ? (
          <div className="rparesourcelive__content">
            {this.state.nearlyRpaScheduleGrid ? (
              <div className="rparesourcelive__match">
                <div className="rparesourcelive__countdown">
                  <span className={countdownClassName}>{this.state.countDownObj.str}</span>
                </div>
                {this.state.activityType === "BADMINTON" && this.state.hasScoring ? <RpaScoringBadminton keyAndPadCenterObj={this.state.keyAndPadCenterObj} /> : ""}
                {this.state.activityType === "FUTSAL" && this.state.hasScoring ? <RpaScoringFutsal keyAndPadCenterObj={this.state.keyAndPadCenterObj} /> : ""}
              </div>
            ) : (
              <div className="rparesourcelive__no-countdown" dangerouslySetInnerHTML={{ __html: this.state.scoringWaitContentHtml }} />
            )}
          </div>
        ) : (
          <>
            <div className="rparesourcelive__no-countdown" dangerouslySetInnerHTML={{ __html: this.state.scoringWaitContentHtml }} />
          </>
        )}
        {this.state.liveContentHtml ? <div className="rparesourcelive__live-content" dangerouslySetInnerHTML={{ __html: this.state.liveContentHtml }} /> : ""}
      </div>
    );
  }
}

var rpaScoringNode = document.getElementById("rpa-scoring");
if (rpaScoringNode) {
  var root = ReactDOM.createRoot(rpaScoringNode);
  root.render(<RpaScoring rpaResourceId={rpaScoringNode.getAttribute("data-rparesourceid")} startTimeRefreshScoring={rpaScoringNode.getAttribute("data-starttimerefreshscoring")} endTimeRefreshScoring={rpaScoringNode.getAttribute("data-endtimerefreshscoring")} />);
}
